import { useState, useEffect, createContext, useContext } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
  getIdTokenResult
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';

// Define user roles
export type UserRole = 'data_manager' | 'slaughterhouse_user';

// Extended user interface with role information
export interface AuthUser extends User {
  role?: UserRole;
  idToken?: string;
}

// Auth context type
interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
  isDataManager: boolean;
  isSlaughterhouseUser: boolean;
  redirectToUserHome: (userRole?: string) => void;
}

// Create auth context
const AuthContext = createContext<AuthContextType | null>(null);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Function to redirect based on user role
  const redirectToUserHome = (userRole?: string) => {
    // Redirigir según el rol del usuario
    if (userRole === 'slaughterhouse_user') {
      console.log('Redirecting slaughterhouse user to certification');
      navigate('/certification', { replace: true });
    } else {
      console.log('Redirecting data manager to dashboard');
      navigate('/dashboard', { replace: true });
    }
  };

  // Function to update user with token
  const updateUserWithToken = async (firebaseUser: User): Promise<AuthUser> => {
    try {
      // Get ID token with custom claims
      let tokenResult;
      let idToken;
      
      try {
        // Intentar obtener el token con reintentos
        tokenResult = await getIdTokenResult(firebaseUser);
        idToken = await getIdToken(firebaseUser);
      } catch (error) {
        console.error('Error inicial al obtener token, reintentando:', error);
        // Esperar un momento y reintentar
        await new Promise(resolve => setTimeout(resolve, 1000));
        tokenResult = await getIdTokenResult(firebaseUser);
        idToken = await getIdToken(firebaseUser);
      }
      
      if (!idToken) {
        throw new Error('No se pudo obtener el token de autenticación');
      }
      
      // Set token in API service and localStorage
      apiService.setAuthToken(idToken);
      
      // Extract role from custom claims
      const role = tokenResult?.claims?.role as UserRole || 'data_manager';
      
      const authUser: AuthUser = {
        ...firebaseUser,
        role,
        idToken,
      };
      
      setUser(authUser);
      setLoading(false);
      return authUser;
    } catch (error) {
      console.error('Error getting user token:', error);
      // Establecer un usuario básico sin token en caso de error
      const basicUser = {
        ...firebaseUser,
        role: 'data_manager', // Rol por defecto
      } as AuthUser;
      setUser(basicUser);
      setLoading(false);
      return basicUser;
    }
  };

  useEffect(() => {
    // Authentication flow
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        await updateUserWithToken(firebaseUser);
      } else {
        setUser(null);
        apiService.clearAuthToken();
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);

      // Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('User signed in:', userCredential.user.email);

      try {
        // Force token refresh to ensure we have the latest claims
        const updatedUser = await updateUserWithToken(userCredential.user);
        // Redirect based on user role
        redirectToUserHome(updatedUser?.role);
      } catch (tokenError) {
        console.error('Error updating user token after sign in:', tokenError);
        // Continuar con el proceso de inicio de sesión aunque haya error en el token
        const basicUser = {
          ...userCredential.user,
          role: 'data_manager', // Rol por defecto en caso de error
        } as AuthUser;
        setUser(basicUser);
        setLoading(false);
        // Redirect based on user role
        redirectToUserHome(basicUser.role);
      }
    } catch (error) {
      console.error('Error during sign in:', error);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      apiService.clearAuthToken();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    if (!user) return null;

    try {
      // Force refresh token and update it in the API service
      let token;
      try {
        token = await getIdToken(user, true);
      } catch (error) {
        console.error('Error inicial al obtener token, reintentando:', error);
        // Esperar un momento y reintentar
        await new Promise(resolve => setTimeout(resolve, 1000));
        token = await getIdToken(user, true);
      }

      if (token) {
        apiService.setAuthToken(token);
      }
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  };

  const isDataManager = user?.role === 'data_manager';
  const isSlaughterhouseUser = user?.role === 'slaughterhouse_user';

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signOut,
    getAuthToken,
    isDataManager,
    isSlaughterhouseUser,
    redirectToUserHome,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};